import { useCallback, useEffect, useState } from 'react'
import api from '../api/client'
import Boton from './Boton'
import Modal from './Modal'
import { fmtFechaHora } from '../utils/fecha'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * Historial de Andreani de un remito (T90) o una NE (T80) — sugerencia #112.
 *
 * Cada evento de la traza, del más nuevo al más viejo, con a quién se avisó.
 * Junto a cada novedad (intento fallido, custodia, sucursal de destino…) el
 * botón "Contacté al cliente" registra el resultado y la marca atendida.
 *
 * Props: url = endpoint GET de eventos ('/remitos/12/andreani/eventos');
 *        suelto = card propia (sin el separador de arriba que lleva dentro de otra card).
 */
const RESULTADOS = [
  { valor: 'coordino_nueva_entrega', label: 'Coordinó nueva entrega' },
  { valor: 'no_responde', label: 'No responde' },
  { valor: 'cambio_direccion', label: 'Cambió la dirección' },
]

export default function AndreaniHistorial({ url, suelto = false }) {
  const [eventos, setEventos] = useState(null)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(null) // evento sobre el que se registra el contacto

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(url)
      setEventos(data)
      setError('')
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo cargar el historial de Andreani.')
    }
  }, [url])

  useEffect(() => { cargar() }, [cargar])

  return (
    <div style={suelto ? undefined : st.bloque}>
      <h4 style={st.titulo}>Historial de Andreani</h4>
      {error && <p style={{ ...st.hint, color: colors.err }}>{error}</p>}
      {!error && eventos === null && <p style={st.hint}>Cargando…</p>}
      {eventos?.length === 0 && <p style={st.hint}>Todavía no hay eventos informados por Andreani.</p>}
      {eventos?.length > 0 && (
        <ol style={st.lista}>
          {eventos.map((ev, i) => (
            <li key={ev.id} style={{ ...st.item, borderBottom: i === eventos.length - 1 ? 'none' : `1px solid ${colors.borderSoft}` }}>
              <span style={{ ...st.punto, background: ev.relevante ? colors.warnStrong : colors.textPlh }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={st.linea1}>
                  <span style={st.estado}>{ev.estado}</span>
                  <span style={st.fecha}>{ev.fecha_evento ? fmtFechaHora(ev.fecha_evento) : 'sin fecha'}</span>
                </div>
                {(ev.motivo || ev.submotivo) && (
                  <div style={st.detalle}>
                    {ev.motivo}{ev.motivo && ev.submotivo ? ' — ' : ''}{ev.submotivo}
                  </div>
                )}
                {ev.sucursal && <div style={st.meta}>Sucursal: {ev.sucursal}</div>}
                {ev.relevante && (
                  <div style={st.meta}>
                    {ev.intento ? `Novedad nº ${ev.intento} · ` : ''}
                    {ev.notificado_at
                      ? `Avisado ${fmtFechaHora(ev.notificado_at)} a ${ev.notificados?.length ? ev.notificados.map(n => n.nombre).join(', ') + ' y admin (Telegram)' : 'admin (Telegram)'}`
                      : 'Sin aviso (historial previo al seguimiento)'}
                  </div>
                )}
                {ev.relevante && (
                  <div style={{ marginTop: space.sm }}>
                    {ev.atendido_at ? (
                      <span style={st.chip} title={ev.nota || undefined}>
                        ✓ {ev.resultado_label} · {ev.atendido_por?.nombre || '—'} · {fmtFechaHora(ev.atendido_at)}
                      </span>
                    ) : (
                      <Boton variante="secondary" sm onClick={() => setAbierto(ev)}>📞 Contacté al cliente</Boton>
                    )}
                    {ev.atendido_at && ev.nota && <div style={{ ...st.meta, marginTop: space.xs }}>“{ev.nota}”</div>}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {abierto && (
        <ModalContacto
          evento={abierto}
          onClose={() => setAbierto(null)}
          onGuardado={async () => { setAbierto(null); await cargar() }}
        />
      )}
    </div>
  )
}

function ModalContacto({ evento, onClose, onGuardado }) {
  const [resultado, setResultado] = useState('')
  const [nota, setNota] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const guardar = async () => {
    if (!resultado) { setError('Elegí el resultado del contacto.'); return }
    setGuardando(true)
    setError('')
    try {
      await api.post(`/andreani-eventos/${evento.id}/atender`, { resultado_contacto: resultado, nota })
      await onGuardado()
    } catch (e) {
      const d = e.response?.data
      setError(d?.message || Object.values(d?.errors || {}).flat().join(' ') || 'No se pudo guardar.')
      setGuardando(false)
    }
  }

  return (
    <Modal title="Contacté al cliente" onClose={onClose} maxWidth="520px">
      <p style={{ ...st.detalle, marginTop: 0 }}>
        <b>{evento.estado}</b>{evento.motivo ? ` — ${evento.motivo}` : ''}{evento.submotivo ? ` (${evento.submotivo})` : ''}
        {evento.fecha_evento ? ` · ${fmtFechaHora(evento.fecha_evento)}` : ''}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, margin: `${space.lg}px 0` }}>
        {RESULTADOS.map(r => (
          <label key={r.valor} style={st.opcion}>
            <input type="radio" name="resultado" value={r.valor} checked={resultado === r.valor} onChange={() => setResultado(r.valor)} />
            {r.label}
          </label>
        ))}
      </div>
      <label style={st.meta}>
        Nota (opcional)
        <textarea
          value={nota}
          onChange={e => setNota(e.target.value)}
          rows={3}
          placeholder="Qué se habló, nueva fecha, nueva dirección…"
          style={st.textarea}
        />
      </label>
      {error && <p style={{ ...st.hint, color: colors.err, marginTop: space.md }}>{error}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.xl }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar'}</Boton>
      </div>
    </Modal>
  )
}

const st = {
  bloque: { marginTop: space.xl, paddingTop: space.lg, borderTop: `1px solid ${colors.border}` },
  titulo: { ...sx.cardTitle, marginBottom: space.md },
  hint: { color: colors.textMeta, fontSize: text.body, margin: 0 },
  lista: { listStyle: 'none', margin: 0, padding: 0 },
  item: { display: 'flex', gap: space.md, padding: `${space.md}px 0` },
  punto: { width: 8, height: 8, borderRadius: radius.pill, marginTop: 5, flexShrink: 0 },
  linea1: { display: 'flex', justifyContent: 'space-between', gap: space.md, alignItems: 'baseline', flexWrap: 'wrap' },
  estado: { fontSize: text.body, fontWeight: 600, color: colors.text },
  fecha: { ...sx.mono, fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap' },
  detalle: { fontSize: text.body, color: colors.textSub, marginTop: 2 },
  meta: { fontSize: text.sec, color: colors.textMeta, marginTop: 2, display: 'block' },
  chip: { display: 'inline-block', fontSize: text.sec, fontWeight: 600, color: colors.textSub, background: colors.borderSoft, borderRadius: radius.xs, padding: '2px 8px' },
  opcion: { display: 'flex', alignItems: 'center', gap: space.md, fontSize: text.body, color: colors.text, cursor: 'pointer' },
  textarea: { display: 'block', width: '100%', boxSizing: 'border-box', marginTop: space.xs, padding: '6px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', color: colors.text },
}
